import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../../../services/api";
import { API_URL } from "@/constants";
import type { MediaProjectListItem } from "../../types";

type ProjectsPageResponse = { items: MediaProjectListItem[] };

// Max ids the `?ids=` filter accepts in one call.
const MAX_IDS_PER_REQUEST = 100;

/**
 * Fills in titles for projects that aren't in the currently loaded
 * Experiences table page (projectTitleById only covers that page), so the
 * insights panel and the notifications bell show a name instead of a raw id.
 */
export function useMissingProjectTitles(
  organizationId: string | undefined,
  projectIds: string[],
  projectTitleById: Record<string, string>
): Record<string, string> {
  // null = asked for, but the API didn't return it.
  const [fetchedTitleById, setFetchedTitleById] = useState<
    Record<string, string | null>
  >({});

  const missingIds = useMemo(
    () =>
      Array.from(new Set(projectIds))
        .filter((id) => !projectTitleById[id] && !(id in fetchedTitleById))
        .sort((a, b) => a.localeCompare(b))
        .slice(0, MAX_IDS_PER_REQUEST),
    [projectIds, projectTitleById, fetchedTitleById]
  );

  useQuery({
    queryKey: ["project-titles", organizationId, missingIds],
    queryFn: async () => {
      // Unknown ids and projects the user can't read are omitted by the API.
      const params = new URLSearchParams();
      missingIds.forEach((id) => params.append("ids", id));
      const { data } = await axiosInstance.get<ProjectsPageResponse>(
        `${API_URL}/media/${organizationId}/projects`,
        { params }
      );
      const titleById: Record<string, string | null> = Object.fromEntries(
        missingIds.map((id) => [id, null])
      );
      data.items.forEach((p) => {
        titleById[p.id] = p.title;
      });
      return titleById;
    },
    enabled: Boolean(organizationId) && missingIds.length > 0,
    onSuccess: (titleById) =>
      setFetchedTitleById((prev) => ({ ...prev, ...titleById })),
    // No cache: a cache hit on remount would skip onSuccess and lose titles.
    cacheTime: 0,
  });

  return useMemo(() => {
    const fetched = Object.fromEntries(
      Object.entries(fetchedTitleById).filter(
        (entry): entry is [string, string] => entry[1] !== null
      )
    );
    return { ...projectTitleById, ...fetched };
  }, [fetchedTitleById, projectTitleById]);
}
